import { Button, Stack, styled, Typography } from '@mui/material';
import { ColDef, GridApi, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AgTable } from '../../../components/AgTable/AgTable';
import { ActionButtonStack, ActionButtonWrapper } from '../../../components/AgTable/gridActionBar';
import { multiRowSelectionDefs } from '../../../components/AgTable/rowSelectionDefs';
import { PageContainer } from '../../../components/Layout/PageContainer';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { SearchInput } from '../../../CoreComponents/base/SearchInput';
import { IField } from '../../../data-models/field2.data-model';
import { IFormulaFieldDataModel } from '../../../data-models/formula-field.data-model';
import { displayedFormulaFieldIdsLoadable } from '../../../services/state/AdminPanel/FormulaFieldsStateJ';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { ErrorOccurredMessage } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { BulkFormulaDisplayModal } from './BulkFormulaDisplayModal';
import { BulkKpiDisplayModal } from './BulkKpiDisplayModal';
import { FieldCreateMenu } from './FieldCreateMenu';
import { FieldEditModal } from './FieldEditModal';
import { FieldTypePills } from './FieldTypePills';
import { FieldKind, FieldPill } from './fieldManagerModels';
import { useFieldManagerGrid } from './useFieldManagerGrid';

const GridContainer = styled('div')`
  width: 100%;
  flex: 1;
  margin-top: 1rem;
`;

const DEFAULT_COL_DEF: ColDef = { flex: 1, minWidth: 150, enableRowGroup: true };

// Stable identity so the bulk modal's seed does not change between renders.
const EMPTY_IDS: ReadonlySet<number> = new Set<number>();

interface EditState {
  kind: FieldKind;
  item: unknown;
}

export function FieldManager() {
  const hasAccess = PermissionService.get().hasPermission(PermissionKey.customerSuccessAll);
  const navigate = useNavigate();
  const [pill, setPill] = useState<FieldPill>('all');
  const [editState, setEditState] = useState<EditState | null>(null);
  const [selectedRows, setSelectedRows] = useState<unknown[]>([]);
  const [bulkEditing, setBulkEditing] = useState(false);
  const [search, setSearch] = useState('');
  const displayedLoad = useAtomValue(displayedFormulaFieldIdsLoadable);
  const gridApiRef = useRef<GridApi | null>(null);

  const onEdit = useCallback((kind: FieldKind, item: unknown) => setEditState({ kind, item }), []);
  const { columnDefs, rowData, getRowId, autoGroupColumnDef, groupDefaultExpanded, isLoading, hasError } =
    useFieldManagerGrid(pill, onEdit);

  // Bulk selection exists only on the KPIs and Formulas pills, and only the
  // display config is bulk-editable — no other part of a field.
  const isKpiPill = pill === 'kpi';
  const isFormulaPill = pill === 'formula';
  const hasBulkDisplay = isKpiPill || isFormulaPill;
  const onPillChange = useCallback((next: FieldPill) => {
    setPill(next);
    setSelectedRows([]);
    setBulkEditing(false);
  }, []);
  const onSelectionChanged = useCallback(
    (e: SelectionChangedEvent<unknown>) => setSelectedRows(e.api.getSelectedRows()),
    []
  );
  const onGridReady = useCallback((e: GridReadyEvent) => {
    gridApiRef.current = e.api;
  }, []);
  // After a bulk apply the rows are still checked in the grid, so the action bar
  // would linger with a stale count. (The KPI bulk modal reloads the page
  // instead, so it never gets here.) Cancel uses the same path.
  const clearSelection = useCallback(() => {
    gridApiRef.current?.deselectAll();
    setSelectedRows([]);
  }, []);
  // Filtering does not deselect in ag-grid, so without this the action bar
  // would keep a count that includes rows the search has hidden — and Apply
  // would write display config for fields the user can no longer see.
  const onSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      clearSelection();
    },
    [clearSelection]
  );

  if (!hasAccess) {
    return null;
  }
  if (hasError) {
    return <ErrorOccurredMessage />;
  }

  return (
    <PageContainer>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant='h4'>Field Manager</Typography>
        <Stack direction='row' gap='0.5rem'>
          {isKpiPill && (
            <Button
              color='secondary'
              variant='outlined'
              onClick={() => navigate(`/${ROUTES.ADMIN_PANEL}/${ROUTES.CONFIG}/${ROUTES.CONFIG_KPI_GROUPS}`)}
            >
              Manage KPI Groups
            </Button>
          )}
          <FieldCreateMenu />
        </Stack>
      </Stack>
      <Stack direction='row' mt='1rem' justifyContent='space-between' alignItems='center'>
        <FieldTypePills selected={pill} onChange={onPillChange} />
        <SearchInput
          size='small'
          margin='none'
          sx={{ width: '14rem' }}
          value={search}
          onChange={onSearchChange}
          placeholder='Search fields'
        />
      </Stack>
      <GridContainer>
        {hasBulkDisplay && selectedRows.length > 0 && (
          <ActionButtonWrapper>
            <ActionButtonStack direction='row'>
              <Button color='secondary' variant='contained' onClick={() => setBulkEditing(true)}>
                {`Edit Field Display (${selectedRows.length})`}
              </Button>
              <Button color='secondary' variant='outlined' onClick={clearSelection}>
                Cancel
              </Button>
            </ActionButtonStack>
          </ActionButtonWrapper>
        )}
        <AgTable
          key={pill}
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDefaultExpanded={groupDefaultExpanded}
          rowData={isLoading ? [] : rowData}
          getRowId={getRowId}
          rowSelection={hasBulkDisplay ? multiRowSelectionDefs : undefined}
          onSelectionChanged={hasBulkDisplay ? onSelectionChanged : undefined}
          onGridReady={onGridReady}
          // A prop, not `gridApi.setGridOption`: the grid is keyed on the pill,
          // so an api-set option would be lost on every pill switch.
          quickFilterText={search}
        />
      </GridContainer>
      {editState && (
        <FieldEditModal kind={editState.kind} item={editState.item} onClose={() => setEditState(null)} />
      )}
      {bulkEditing && isKpiPill && selectedRows.length > 0 && (
        <BulkKpiDisplayModal kpis={selectedRows as IField<unknown>[]} onClose={() => setBulkEditing(false)} />
      )}
      {bulkEditing && isFormulaPill && selectedRows.length > 0 && (
        <BulkFormulaDisplayModal
          onApplied={clearSelection}
          fields={selectedRows as IFormulaFieldDataModel[]}
          displayedIds={displayedLoad.state === 'hasData' ? (displayedLoad.data ?? EMPTY_IDS) : EMPTY_IDS}
          onClose={() => setBulkEditing(false)}
        />
      )}
    </PageContainer>
  );
}
